import React, { useState, useContext, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import axios from "axios";
import { AuthContext } from "react-oauth2-code-pkce";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://familyduniya-dev.censeotech.com/api-family";

function getCroppedImg(imageSrc, croppedAreaPixels) {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      const ctx = canvas.getContext("2d");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x * scaleX,
        croppedAreaPixels.y * scaleY,
        croppedAreaPixels.width * scaleX,
        croppedAreaPixels.height * scaleY,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      resolve(canvas.toDataURL("image/jpeg"));
    };
  });
}

const CreateFamilyTree = ({ onSuccess, onClose, familyId }) => {
  const { token } = useContext(AuthContext);
  const isEditMode = Boolean(familyId);

  const [familyName, setFamilyName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const [rawImage, setRawImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback(
    (_, croppedPixels) => setCroppedAreaPixels(croppedPixels),
    []
  );

  // ✅ Fetch existing family details when editing
  useEffect(() => {
    if (!isEditMode || !familyId) return;
    const fetchFamily = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/family/${familyId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        // backend may return data in {data:{...}} or directly {...}
        const data = res.data?.data || res.data;
        setFamilyName(data.familyName || "");
        setDescription(data.description || "");
        setIsPublic(data.isPublic || false);
        setAvatarPreview(data.avatarFile || null);
        setPassword(data.publicAccessPassword || "");
      } catch (err) {
        console.error("Error fetching family:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFamily();
  }, [familyId, isEditMode, token]);

  // ✅ Handle avatar upload + crop
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setRawImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async () => {
    if (!rawImage || !croppedAreaPixels) return;

    const croppedImg = await getCroppedImg(rawImage, croppedAreaPixels);
    setAvatarPreview(croppedImg);

    const arr = croppedImg.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    setAvatar(new File([u8arr], "avatar.jpg", { type: mime }));
    setShowCropper(false);
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    setPasswordError(
      val.length && val.length !== 6 ? "Password must be exactly 6 digits." : ""
    );
  };

  // ✅ Submit (POST for create / PUT for update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditMode && !avatar) {
      toast.error("Please upload a family avatar.");
      return;
    }
    if (!familyName) {
      toast.error("Please enter family Name");
      return;
    }
    if (!description) {
      toast.error("Please enter family Description");
      return;
    }

    if (passwordError) return;
    const formData = new FormData();
    formData.append("name", familyName);
    formData.append("description", description);
    formData.append("publicView", isPublic);
    formData.append("publicAccessPassword", password);
    if (isEditMode){
        formData.append("id", familyId); }
    if (avatar) formData.append("avatar", avatar);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const url = isEditMode
        ? `${API_BASE_URL}/family`
        : `${API_BASE_URL}/family`;
      const method = isEditMode ? "put" : "post";

      const res = await axios({
        method,
        url,
        headers,
        data: formData,
      });

      if (![200, 201].includes(res.status))
        throw new Error("Failed to save family.");
      onSuccess?.(
        isEditMode
          ? "Family updated successfully!"
          : "Family created successfully!"
      );
      onClose?.();
    } catch (err) {
      alert(err?.response?.data?.message || err.message);
    }
  };

  const avatarLetter = familyName?.trim()?.[0]?.toUpperCase() || "?";

  if (loading)
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/40 text-white text-lg">
        Loading family details...
      </div>
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 relative z-50 overflow-y-auto max-h-[90vh]"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-red-500 text-2xl font-bold"
        >
          &times;
        </button>

        <div className="text-center mb-5">
          <h2 className="text-xl sm:text-2xl font-bold text-indigo-700">
            {isEditMode ? "✏️ Edit Family Tree" : "🌳 Create Family Tree"}
          </h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            {isEditMode
              ? "Update your family details and avatar."
              : "Build your family legacy. Add members, choose visibility, and upload an avatar."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Family Info */}
          <div className="space-y-3">
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Family Name"
              className="w-full px-3 py-2 border rounded-lg border-indigo-300 focus:ring-2 focus:ring-indigo-200 text-sm sm:text-base"
              required
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              rows={3}
              className="w-full px-3 py-2 border rounded-lg border-indigo-300 focus:ring-2 focus:ring-indigo-200 text-sm sm:text-base resize-none"
            />
          </div>

          {/* Privacy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={() => setIsPublic(!isPublic)}
                className="toggle toggle-success"
              />
              <span className="text-sm sm:text-base text-gray-700">
                Publicly visible
              </span>
            </div>
            <input
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="6-digit view password"
              className="w-full px-3 py-2 border rounded-lg border-green-300 focus:ring-2 focus:ring-green-200 text-sm sm:text-base"
            />
            {passwordError && (
              <p className="text-red-500 text-xs mt-1">{passwordError}</p>
            )}
          </div>

          {/* Avatar */}
          <div className="space-y-3">
            {/* Avatar Upload Section */}
            <div className="flex justify-between items-center gap-4">
              {/* Hidden input */}
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />

              {/* Stylish Browse Button */}
              <label
                htmlFor="avatar-upload"
                className="cursor-pointer inline-flex items-center justify-center gap-2 px-4 py-2.5
                           bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-full
                           text-sm font-semibold shadow-md hover:shadow-lg hover:from-purple-700 hover:to-purple-800
                           active:scale-95 transition-all duration-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Browse Image
              </label>

              {/* Image preview aligned to right */}
              <div className="flex justify-end flex-1">
                {avatarPreview ? (
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-purple-300 shadow-md">
                    <img
                      src={avatarPreview}
                      alt="Avatar Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {avatarLetter}
                  </div>
                )}
              </div>
            </div>

          </div>

          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-full text-sm sm:text-base font-semibold"
          >
            {isEditMode ? "UPDATE FAMILY" : "CREATE FAMILY"}
          </button>
        </form>

        {/* Cropper */}
        {showCropper && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-2">
            <div className="bg-white p-3 rounded-2xl shadow-lg w-full max-w-sm sm:max-w-md">
              <div className="relative w-full h-64 sm:h-80 bg-gray-100 rounded-lg overflow-hidden">
                <Cropper
                  image={rawImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="flex justify-end mt-3 gap-2">
                <button
                  onClick={() => setShowCropper(false)}
                  className="px-3 py-1 bg-gray-300 rounded-lg text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropConfirm}
                  className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm sm:text-base"
                >
                  Crop
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CreateFamilyTree;
